import React, { useState, useEffect, useRef } from 'react';
interface SearchableSelectProps {
  value: string;
  onChange: (value: string) => void;
  options: string[];
  placeholder: string;
  disabled?: boolean;
  allowCustomInput?: boolean;
  allowSearch?: boolean; // Prop para permitir búsqueda sin valores personalizados
  error?: boolean;
  openUpward?: boolean; // Prop para abrir el dropdown hacia arriba (por defecto false, abre hacia abajo)
  /** Elemento opcional que se muestra dentro del campo a la izquierda (ej. círculo de color) */
  prefix?: React.ReactNode;
  /** Si se define, cada opción del desplegable muestra un círculo con este color a la izquierda */
  getOptionColor?: (option: string) => string | undefined;
}
const SearchableSelect: React.FC<SearchableSelectProps> = ({ 
  value, 
  onChange, 
  options, 
  placeholder,
  disabled = false,
  allowCustomInput = false,
  allowSearch = false,
  error = false,
  openUpward = false,
  prefix,
  getOptionColor
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [isTyping, setIsTyping] = useState(false); // Para rastrear si el usuario está escribiendo
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  // Filtrar opciones basándose en el término de búsqueda
  const filteredOptions = options.filter(option => 
    option.toLowerCase().includes(searchTerm.toLowerCase())
  );
  // Determinar qué opciones mostrar:
  // - Si no se permite escribir ni buscar: mostrar todas las opciones cuando está abierto
  // - Si se permite buscar: mostrar opciones filtradas basadas en searchTerm
  // - Si searchTerm está vacío: mostrar todas las opciones
  const displayOptions = (searchTerm === '' || (!allowCustomInput && !allowSearch)) ? options : filteredOptions;
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        // Resetear el término de búsqueda cuando se cierra
        setSearchTerm('');
        setIsTyping(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [value, allowCustomInput, allowSearch]);
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Solo permitir escribir si allowCustomInput o allowSearch es true
    if (!allowCustomInput && !allowSearch) {
      return;
    }
    const newSearchTerm = e.target.value;
    setSearchTerm(newSearchTerm);
    setIsTyping(true); // El usuario está escribiendo
    setIsOpen(true);
    setHighlightedIndex(-1);
    // Si allowCustomInput es true, permitir valores personalizados
    if (allowCustomInput) {
      onChange(newSearchTerm);
    }
  };
  const handleOptionClick = (option: string) => {
    onChange(option);
    setSearchTerm('');
    setIsTyping(false);
    setIsOpen(false);
  };
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // Obtener las opciones a mostrar
    const currentOptions = (!allowCustomInput && !allowSearch && searchTerm === '') ? options : filteredOptions;
    // Si no se permite escribir, solo permitir navegación con teclado
    if (!allowCustomInput && !allowSearch) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setIsOpen(true);
        setHighlightedIndex(prev => (prev < currentOptions.length - 1 ? prev + 1 : 0));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setIsOpen(true);
        setHighlightedIndex(prev => (prev > 0 ? prev - 1 : currentOptions.length - 1));
      } else if (e.key === 'Enter') {
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < currentOptions.length) {
          handleOptionClick(currentOptions[highlightedIndex]);
        }
      } else if (e.key === 'Escape') {
        setIsOpen(false);
        setSearchTerm('');
        setIsTyping(false);
      } else {
        // Prevenir cualquier otra tecla si no se permite escribir
        e.preventDefault();
      }
      return;
    }
    // Comportamiento normal cuando se permite escribir
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setIsOpen(true);
      setHighlightedIndex(prev => (prev < currentOptions.length - 1 ? prev + 1 : 0));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setIsOpen(true);
      setHighlightedIndex(prev => (prev > 0 ? prev - 1 : currentOptions.length - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (highlightedIndex >= 0 && highlightedIndex < currentOptions.length) {
        handleOptionClick(currentOptions[highlightedIndex]);
      } else if (allowCustomInput && searchTerm.trim() !== '') {
        onChange(searchTerm);
        setIsOpen(false);
      }
    } else if (e.key === 'Escape') {
      setIsOpen(false);
      setSearchTerm('');
      setIsTyping(false);
    }
  };
  const handleInputFocus = () => {
    if (!disabled) {
      setIsOpen(true);
      // Cuando se abre el campo, resetear searchTerm para mostrar todas las opciones
      // El valor seleccionado se mostrará en el input a través de displayValue
      setSearchTerm('');
      setIsTyping(false); // No está escribiendo aún, solo abrió el campo
      setHighlightedIndex(-1);
    }
  };
  // Determinar qué mostrar en el input:
  // - Si está abierto y el usuario está escribiendo: mostrar searchTerm (puede estar vacío para buscar)
  // - Si está abierto pero no está escribiendo: mostrar el valor seleccionado (si existe)
  // - Si está cerrado: mostrar el valor seleccionado (si existe) o vacío para placeholder
  const displayValue = isOpen 
    ? (isTyping ? searchTerm : (value || '')) 
    : (value || '');
  // Determinar si el input está vacío (sin valor seleccionado)
  const isEmpty = !value || value.trim() === '';
  const inputStyle: React.CSSProperties = {
    cursor: disabled ? 'not-allowed' : (!allowCustomInput && !allowSearch ? 'pointer' : 'text'),
    backgroundColor: disabled ? '#f3f4f6' : '#ffffff',
    color: isEmpty && !isOpen ? 'transparent' : '#1e293b'
  };
  if (prefix) {
    inputStyle.border = 'none';
    inputStyle.borderRadius = 0;
    inputStyle.paddingLeft = 8;
    inputStyle.boxShadow = 'none';
  }
  const trigger = (
    <>
      {prefix ? (
        <div style={{ display: 'flex', alignItems: 'center', paddingLeft: 12, paddingRight: 8, flexShrink: 0 }}>
          {prefix}
        </div>
      ) : null}
      <div style={{ position: 'relative', flex: 1, minWidth: 0 }}>
        <input
          ref={inputRef}
          type="text"
          className={`form-input ${error ? 'error' : ''}`}
          value={displayValue}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          readOnly={!allowCustomInput && !allowSearch}
          style={inputStyle}
        />
      </div>
    </>
  );
  const wrapperStyle: React.CSSProperties = { position: 'relative', width: '100%' };
  if (prefix) {
    wrapperStyle.display = 'flex';
    wrapperStyle.alignItems = 'stretch';
    wrapperStyle.border = '1px solid #e5e7eb';
    wrapperStyle.borderRadius = 6;
    wrapperStyle.background = disabled ? '#f3f4f6' : '#ffffff';
    if (!disabled) wrapperStyle.cursor = 'pointer';
  }
  const handleWrapperClick = () => {
    if (prefix && !disabled && inputRef.current) {
      inputRef.current.focus();
      setIsOpen(true);
    }
  };

  return (
    <div
      style={wrapperStyle}
      ref={containerRef}
      className={prefix ? 'searchable-select-with-prefix' : undefined}
      onClick={prefix ? handleWrapperClick : undefined}
    >
      {prefix ? trigger : (
        <input
          ref={inputRef}
          type="text"
          className={`form-input ${error ? 'error' : ''}`}
          value={displayValue}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          readOnly={!allowCustomInput && !allowSearch}
          style={inputStyle}
        />
      )}
      {isOpen && !disabled && (
        <div
          className="searchable-select-dropdown"
          style={{
            position: 'absolute',
            ...(openUpward ? {
              bottom: '100%',
              marginBottom: '4px',
              borderRadius: '6px 6px 0 0',
              boxShadow: '0 -4px 6px rgba(0,0,0,0.1)',
            } : {
              top: '100%',
              marginTop: '4px',
              borderRadius: '0 0 6px 6px',
              boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
            }),
            left: 0,
            right: 0,
            backgroundColor: '#fff',
            border: '1px solid #e5e7eb',
            zIndex: 1000,
            maxHeight: '180px',
            overflowY: 'auto',
            overflowX: 'hidden',
          }}
        >
          {displayOptions.length > 0 ? (
            displayOptions.map((option, index) => {
              const optionColor = getOptionColor?.(option);
              return (
                <div
                  key={option}
                  className="searchable-select-option"
                  onClick={() => handleOptionClick(option)}
                  style={{
                    padding: '8px 12px',
                    cursor: 'pointer',
                    backgroundColor: index === highlightedIndex ? '#f1f5f9' : 'transparent',
                    fontFamily: 'Montserrat, sans-serif',
                    fontSize: '13px',
                    color: '#374151',
                    transition: 'background-color 0.2s',
                    display: 'flex',
                    alignItems: 'center',
                    gap: optionColor != null ? 8 : 0
                  }}
                  onMouseEnter={() => setHighlightedIndex(index)}
                >
                  {optionColor != null ? (
                    <>
                      <span
                        style={{
                          width: 12,
                          height: 12,
                          borderRadius: '50%',
                          backgroundColor: optionColor,
                          flexShrink: 0
                        }}
                        aria-hidden
                      />
                      {option}
                    </>
                  ) : (
                    option
                  )}
                </div>
              );
            })
          ) : (
            <div
              style={{
                padding: '8px 12px',
                fontFamily: 'Montserrat, sans-serif',
                fontSize: '13px',
                color: '#6b7280',
                fontStyle: 'italic',
                textAlign: 'center'
              }}
            >
              No se encontraron resultados
            </div>
          )}
        </div>
      )}
    </div>
  );
};
export default SearchableSelect;
